import { Plugin, PluginKey } from 'prosemirror-state'
import { kHighlightClassSelection } from '@/inline-edit/constants/highlight'
import { Decoration, DecorationSet } from 'prosemirror-view'

export const inlineEditHighlightKey = new PluginKey('inlineEditHighlight')

/**
 * ProseMirror plugin that manages a highlight decoration for inline edit selections.
 * Set the highlight by dispatching a transaction with meta:
 *   view.dispatch(view.state.tr.setMeta(inlineEditHighlightKey, { from: 10, to: 50 }))
 * Clear it with:
 *   view.dispatch(view.state.tr.setMeta(inlineEditHighlightKey, null))
 */
export const inlineEditHighlightPlugin = new Plugin({
  key: inlineEditHighlightKey,
  state: {
    init() {
      return DecorationSet.empty
    },
    apply(tr, decorationSet) {
      const meta = tr.getMeta(inlineEditHighlightKey)
      if (meta === undefined) {
        // No meta — map existing decorations through document changes
        return decorationSet.map(tr.mapping, tr.doc)
      }
      if (meta === null) {
        return DecorationSet.empty
      }
      const { from, to, className } = meta as { from: number; to: number; className?: string }
      const cls = className ?? kHighlightClassSelection
      const decorations: Decoration[] = [Decoration.inline(from, to, { class: cls })]
      // Inline decorations can't wrap contenteditable=false nodes, so emit per-node
      // decorations for referenceMarker badges inside the range — the NodeView reads
      // these and composes the class onto its dom.
      tr.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'referenceMarker') {
          decorations.push(Decoration.node(pos, pos + node.nodeSize, {}, { class: cls }))
        }
      })
      return DecorationSet.create(tr.doc, decorations)
    },
  },
  props: {
    decorations(state) {
      return inlineEditHighlightKey.getState(state)
    },
  },
})
